import { boolean, number, object, string } from "yup";

import IServiceQueue, {
  ICustomerUserServiceQueue,
  IServiceQueueInput,
} from "@/interfaces/service-queue.interface";
import { ServiceQueueFormMessages } from "@/messages/service-queue/service-queue-form.messages";
import { SelectData } from "@/types/form.types";

import { ServiceQueueFormData } from "./types";

export const serviceQueueFormNames: Record<keyof ServiceQueueFormData, string> = {
  name: "name",
  active: "active",
  routingType: "routingType",
  sla: "sla",
  users: "users",
};

/** Mesmo default da migration, para a fila nova nascer com o SLA que o dashboard já usava. */
export const DEFAULT_QUEUE_SLA_MINUTES = 15;
export const MAX_QUEUE_SLA_MINUTES = 1440;

export const ROUTING_TYPE_OPTIONS = (
  messages: ServiceQueueFormMessages
): SelectData<string>[] => [
  { value: "C", label: messages.routingTypeCircular },
  { value: "M", label: messages.routingTypeLeastAttendance },
];

export const getRoutingTypeLabel = (
  routingType: string,
  messages: { routingTypeCircular: string; routingTypeLeastAttendance: string }
): string => {
  if (routingType === "C") return messages.routingTypeCircular;
  if (routingType === "M") return messages.routingTypeLeastAttendance;
  return routingType || "-";
};

export const buildFormInitialData = (
  serviceQueue?: IServiceQueue | null
): ServiceQueueFormData => {
  return {
    name: serviceQueue?.name || "",
    active: serviceQueue?.active ?? true,
    routingType: serviceQueue?.routingType || "C",
    sla: serviceQueue?.sla ?? DEFAULT_QUEUE_SLA_MINUTES,
    users: serviceQueue?.users || [],
  };
};

export const buildServiceQueueFormSchema = (messages: ServiceQueueFormMessages) =>
  object().shape({
    name: string().required(messages.errorRequiredName),
    active: boolean().required(),
    routingType: string().required(messages.errorRequiredRoutingType),
    sla: number()
      .typeError(messages.errorInvalidSla)
      .required(messages.errorRequiredSla)
      .integer(messages.errorInvalidSla)
      .min(1, messages.errorInvalidSla)
      .max(MAX_QUEUE_SLA_MINUTES, messages.errorInvalidSla),
  });

export const normalizeServiceQueueFormData = (
  formData: ServiceQueueFormData
): IServiceQueueInput => {
  return {
    name: formData.name,
    active: formData.active,
    routingType: formData.routingType,
    sla: Number(formData.sla) || DEFAULT_QUEUE_SLA_MINUTES,
    users: (formData.users || []).map((user: ICustomerUserServiceQueue) => ({
      userID: user.userID,
    })),
  };
};
